import React from 'react';
import type { TFunction } from 'i18next';
import { Api, Domain } from '@3nickels/data-modules';
import { Svgs } from '../../../../assets/svg';
import { ManualAccountIcon } from '../../accounts/ManualAccountTypes';
import { PlaidAccountStatusEnum } from '../Types';
import { ContinueLinkingItem, ManualMenuOption } from './Types';
import { mapAutoLinkResultRestToAccounts } from '../PlaidLinkingUtils';

type ManualCategoryCardConfig = {
  icon: React.ReactNode;
  menuItems?: ManualMenuOption[];
  onClick?: () => Promise<void> | void;
  previewItems: string[];
  title: string;
};

type GetManualCategoryCardsArgs = {
  debtMenuItems: ManualMenuOption[];
  handleClose: () => void;
  investmentsMenuItems: ManualMenuOption[];
  onManualNavigation?: (target: ManualMenuOption['target']) => Promise<void> | void;
  otherMenuItems: ManualMenuOption[];
  propertiesMenuItems: ManualMenuOption[];
  retirementMenuItems: ManualMenuOption[];
  t: TFunction;
};

type GetContinueLinkingItemsArgs = {
  combinedHousingAccounts: Domain.HousingData[];
  institutions: { accessId: number; institutionLogo?: string; institutionName?: string }[];
  plaidApi: Api.PlaidApi;
};

function getAccountStatus(
  account: Domain.IAutoBindableAccount,
  combinedHousingAccounts: Domain.HousingData[]
): Domain.PlaidAccountStatusEnum {
  const existingHousing = combinedHousingAccounts.find(
    (x) => x.debt?.id === account.financialAccountId
  );
  if (existingHousing) {
    return Domain.PlaidAccountStatusEnum.Complete;
  }

  if (
    account.autoLinkStatus === 'notLinked' &&
    account.aggType !== 'INVESTMENT_ACCOUNT' &&
    account.aggSubType !== 'mortgage'
  ) {
    return Domain.PlaidAccountStatusEnum.Unlinked;
  }

  if (account.autoLinkStatus === 'notLinked') {
    return Domain.PlaidAccountStatusEnum.NotImported;
  }

  if (account.aggSubType === 'mortgage') {
    return Domain.PlaidAccountStatusEnum.Incomplete;
  }

  if (account.autoLinkStatus !== 'inComplete') {
    return Domain.PlaidAccountStatusEnum.Complete;
  }

  return Domain.PlaidAccountStatusEnum.NotImported;
}

function deferUntilAfterPaint(callback: () => void) {
  if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(() => {
      callback();
    });
    return;
  }

  setTimeout(callback, 0);
}

function getRetirementMenuItems(t: TFunction): ManualMenuOption[] {
  return [
    {
      id: '401k',
      label: t('FourOhOneKFourOhThreeBEtc').toString(),
      target: 'retirement-savings-plan',
    },
    { id: 'ira', label: t('IRA').toString(), target: 'retirement-savings-plan' },
    { id: 'pension', label: t('PensionPlan').toString(), target: 'pension-plan' },
    { id: 'annuity', label: t('Annuity').toString(), target: 'fixed-annuity' },
  ];
}

function getPropertiesMenuItems(t: TFunction): ManualMenuOption[] {
  return [
    { id: 'car', label: t('Car').toString(), target: 'car' },
    { id: 'house', label: t('House').toString(), target: 'home' },
    {
      id: 'investment-property',
      label: t('InvestmentProperty').toString(),
      target: 'investment-real-estate',
    },
    { id: 'other-asset', label: t('OtherAsset').toString(), target: 'other-asset' },
  ];
}

function getInvestmentsMenuItems(t: TFunction): ManualMenuOption[] {
  return [
    {
      id: 'brokerage',
      label: t('BrokerageAccount').toString(),
      target: 'brokerage-account',
    },
    {
      id: 'college-savings',
      label: t('CollegeSavingsPlan').toString(),
      target: 'college-savings-plan',
    },
    {
      id: 'hsa',
      label: t('ManualMenuHSA').toString(),
      target: 'health-savings-account',
    },
    {
      id: 'business',
      label: t('OwnershipInABusiness').toString(),
      target: 'business',
    },
  ];
}

function getDebtMenuItems(t: TFunction): ManualMenuOption[] {
  return [
    { id: 'car-loan', label: t('CarLoan').toString(), target: 'car' },
    {
      id: 'credit-card',
      label: t('CreditCard').toString(),
      target: 'debt-credit-card',
    },
    {
      id: 'medical-debt',
      label: t('MedicalDebt').toString(),
      target: 'debt-medical',
    },
    { id: 'mortgage', label: t('Mortgage').toString(), target: 'mortgage-type' },
    {
      id: 'personal-loan',
      label: t('PersonalLoan').toString(),
      target: 'debt-personal-loan',
    },
    {
      id: 'student-loan',
      label: t('StudentLoan').toString(),
      target: 'debt-student-loan',
    },
    {
      id: 'other-debt',
      label: t('OtherDebt').toString(),
      target: 'debt-other',
    },
  ];
}

function getOtherMenuItems(t: TFunction): ManualMenuOption[] {
  return [
    { id: 'other-asset', label: t('OtherAsset').toString(), target: 'other-asset' },
    {
      id: 'other-debt',
      label: t('OtherDebt').toString(),
      target: 'debt-other',
    },
  ];
}

function getManualCategoryCards({
  debtMenuItems,
  handleClose,
  investmentsMenuItems,
  onManualNavigation,
  otherMenuItems,
  propertiesMenuItems,
  retirementMenuItems,
  t,
}: GetManualCategoryCardsArgs): ManualCategoryCardConfig[] {
  return [
    {
      icon: ManualAccountIcon('CashAccount'),
      onClick: async () => {
        handleClose();
        await onManualNavigation?.('cash-account');
      },
      previewItems: [t('BankAccount').toString(), t('CD').toString()],
      title: t('CashAndBanking').toString(),
    },
    {
      icon: ManualAccountIcon('RetirementPlan'),
      menuItems: retirementMenuItems,
      previewItems: [
        t('ManualCategoryCard401k').toString(),
        t('IRA').toString(),
        t('Annuity').toString(),
      ],
      title: t('RetirementPlansLabel').toString(),
    },
    {
      icon: (
        <Svgs.DisplaySmallCustomHouseKeys className='svg-img' style={{ height: 32, width: 32 }} />
      ),
      menuItems: propertiesMenuItems,
      previewItems: [t('Car').toString(), t('ManualCategoryCardRealEstate').toString()],
      title: t('PropertiesAndAssets').toString(),
    },
    {
      icon: ManualAccountIcon('Investment'),
      menuItems: investmentsMenuItems,
      previewItems: [
        t('Brokerage').toString(),
        t('ManualCategoryCardHSA').toString(),
        t('ManualCategoryCard529').toString(),
      ],
      title: t('InvestmentsAndSavings').toString(),
    },
    {
      icon: ManualAccountIcon('Debt'),
      menuItems: debtMenuItems,
      previewItems: [t('CreditCards').toString(), t('StudentLoans').toString()],
      title: t('Debt').toString(),
    },
    {
      icon: <Svgs.DisplaySmallCustomCoins className='svg-img' style={{ height: 32, width: 32 }} />,
      menuItems: otherMenuItems,
      previewItems: [t('OtherAsset').toString(), t('OtherDebt').toString()],
      title: t('Other').toString(),
    },
  ];
}

async function getContinueLinkingItems({
  combinedHousingAccounts,
  institutions,
  plaidApi,
}: GetContinueLinkingItemsArgs): Promise<ContinueLinkingItem[]> {
  const results: ContinueLinkingItem[][] = await Promise.all(
    institutions.map(async (institution) => {
      try {
        const { data } = await plaidApi.getInstitution(institution.accessId);
        const availableAccounts: ContinueLinkingItem[] = mapAutoLinkResultRestToAccounts(data)
          .map((account: Domain.IAutoBindableAccount) => ({
            accessId: institution.accessId,
            accountName: account.name,
            institutionLogo: institution.institutionLogo,
            institutionName: institution.institutionName ?? '',
            status: getAccountStatus(account, combinedHousingAccounts),
          }))
          .filter(
            (account: ContinueLinkingItem) =>
              account.status === PlaidAccountStatusEnum.Incomplete ||
              account.status === PlaidAccountStatusEnum.NotImported
          );

        return availableAccounts;
      } catch (error) {
        console.error('Failed to load institution accounts for modal', institution.accessId, error);
        return [] as ContinueLinkingItem[];
      }
    })
  );

  return results
    .flat()
    .sort((left: ContinueLinkingItem, right: ContinueLinkingItem) =>
      left.accountName.localeCompare(right.accountName)
    );
}

export {
  deferUntilAfterPaint,
  getContinueLinkingItems,
  getDebtMenuItems,
  getInvestmentsMenuItems,
  getManualCategoryCards,
  getOtherMenuItems,
  getPropertiesMenuItems,
  getRetirementMenuItems,
};

export type { ManualCategoryCardConfig };
